import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '@/store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.wrenchlab.net';
const HUB_URL = `${API_BASE_URL}/workshophub`;

type Handler = (...args: unknown[]) => void;

interface SignalRContextValue {
  isConnected: boolean;
  on: (event: string, handler: Handler) => void;
  off: (event: string, handler: Handler) => void;
  invoke: (method: string, ...args: unknown[]) => Promise<unknown>;
}

const SignalRContext = createContext<SignalRContextValue>({
  isConnected: false,
  on: () => {},
  off: () => {},
  invoke: async () => undefined,
});

export function SignalRProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const handlersRef = useRef<Map<string, Set<Handler>>>(new Map());
  const token = useAuthStore((s) => s.token);
  const staffToken = useAuthStore((s) => s.staffToken);

  useEffect(() => {
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => useAuthStore.getState().token ?? '',
        headers: staffToken ? { 'X-Staff-Token': staffToken } : {},
        transport: signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => Math.min(1000 * 2 ** ctx.previousRetryCount, 30_000),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(() => setIsConnected(true));
    connection.onclose(() => setIsConnected(false));

    handlersRef.current.forEach((handlers, event) => {
      handlers.forEach((handler) => connection.on(event, handler));
    });

    connection.start()
      .then(() => setIsConnected(true))
      .catch((err) => console.warn('[SignalR] Connection failed:', err));

    connectionRef.current = connection;

    return () => {
      connection.stop().catch(() => {});
      connectionRef.current = null;
      setIsConnected(false);
    };
  }, [token, staffToken]);

  const on = useCallback((event: string, handler: Handler) => {
    if (!handlersRef.current.has(event)) handlersRef.current.set(event, new Set());
    handlersRef.current.get(event)!.add(handler);
    connectionRef.current?.on(event, handler);
  }, []);

  const off = useCallback((event: string, handler: Handler) => {
    handlersRef.current.get(event)?.delete(handler);
    connectionRef.current?.off(event, handler);
  }, []);

  const invoke = useCallback(async (method: string, ...args: unknown[]): Promise<unknown> => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current.invoke(method, ...args);
    }
  }, []);

  return (
    <SignalRContext.Provider value={{ isConnected, on, off, invoke }}>
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalR() {
  return useContext(SignalRContext);
}
